import { ReactSVG } from "react-svg";
import { ExtendedLink } from "../../components/ExtededLink/ExtendedLink";
import { Button, ButtonTypes } from "../../components/Global/Button/Button";
import { MediaItem } from "../../components/Global/MediaItem/MediaItem";
import { SVGIcon } from "../../components/SVGIcon/SVGIcon";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import { customSanitize } from "../../utils/customSanitize";
import { MediaFormatType } from "@/types/ModuleOptionsType";
import { ImageGridType } from "./ImageGrid";
import { downloadFile } from "../../utils/downloadFile";
import styles from "./ImageGridCard.module.scss";

interface ImageGridCardProps {
  element: Exclude<ImageGridType["image_grid"]["elements"], false>[number];
  gridType: ImageGridType["image_grid"]["grid_type"];
  showBoxShadow: boolean | undefined;
  showBorderRadius: boolean | undefined;
  showBox: boolean | undefined;
  mediaFormat: MediaFormatType;
  textAlignClass: string;
  showExtendedLink: boolean;
  type: "image" | "download";
  buttonType: ButtonTypes;
}

const ImageGridCard = ({
  showBoxShadow,
  showBorderRadius,
  element,
  mediaFormat,
  gridType,
  showExtendedLink,
  textAlignClass,
  type,
  buttonType,
  showBox,
}: ImageGridCardProps) => {
  const { getLabel, getIcon } = useGlobalContext();

  const titleInImage = gridType === "title-in-image" && type === "image";

  const btnJSX = element.link && (
    <Button
      type={buttonType}
      href={element.link.url}
      target={element.link.target}
      aria-label={
        element.link.aria_label ??
        `${getLabel("read_more_label")}: ${element.title}`
      }
      className={`${styles["link-btn"]} ${titleInImage ? "mt-2" : ""}`}
      disablePrefetch // Disable prefetching for grid links https://trello.com/c/g5k075FS
    >
      {getLabel("read_more_label")}
    </Button>
  );

  return (
    <div
      data-shadow={showBox && showBoxShadow}
      data-border-radius={showBox && showBorderRadius}
      className={`${styles["image-grid-element"]}`}
    >
      {element.download.link_target && (
        <button
          type={"button"}
          className={`button-overlay`}
          onClick={() => {
            if (element?.download?.link_target)
              downloadFile(element.download.link_target.url).catch((e) => {
                console.error("Download failed:", e);
              });
          }}
          aria-label={getLabel("download_post_aria_label").replace(
            "${title}",
            element?.download?.link_aria_label,
          )}
        >
          {element?.download?.link_aria_label}
        </button>
      )}

      <div className={`${styles["image-wrapper"]} `}>
        {titleInImage && (
          <>
            <div className={styles.overlay}></div>
            <div className={`${styles["btn-wrapper"]}`}>
              <span
                className={`title`}
                dangerouslySetInnerHTML={{
                  __html: customSanitize(element.title),
                }}
              />
              {btnJSX}
              {showExtendedLink && element.link_extended && (
                <ExtendedLink
                  extendedLink={element.link_extended}
                  btnType={buttonType}
                  extraClass="mt-2"
                />
              )}
            </div>
          </>
        )}

        {element.image && (
          <>
            {element.image.mime_type === "image/svg+xml" ? (
              <SVGIcon src={element.image.url} />
            ) : (
              <MediaItem
                image_desktop={element.image}
                mediaFormat={mediaFormat}
                className={
                  type === "download" ? "has-border-radius has-box-shadow" : ""
                }
                sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                copyrightPosition={
                  gridType === "title-in-image" ? "top" : "bottom"
                }
              />
            )}
          </>
        )}
      </div>

      {gridType === "text-below-image" && (
        <div className={`${styles["content-wrapper"]} ${textAlignClass}`}>
          {type !== "download" && element.title && (
            <span
              className={`title`}
              dangerouslySetInnerHTML={{
                __html: customSanitize(element.title),
              }}
            />
          )}

          {type === "download" && element?.download?.link_target && (
            <button
              type="button"
              className={`${styles["download-wrapper"]} ${"m-0"}`}
              onClick={() => {
                if (element?.download?.link_target)
                  downloadFile(element.download.link_target.url).catch((e) => {
                    console.error("Download failed:", e);
                  });
              }}
              aria-label={element?.download?.link_aria_label}
            >
              <ReactSVG
                className={styles["download-icon"]}
                src={getIcon("download")}
                wrapper={"svg"}
                width={24}
                height={24}
              />
              <span className={styles["download-label"]}>
                {element.title || getLabel("fallback_generic_download_label")}
              </span>
            </button>
          )}

          {type === "image" && (
            <>
              <div
                data-align={textAlignClass}
                className={styles["btn-wrapper"]}
              >
                {btnJSX}
                {showExtendedLink && element.link_extended && (
                  <ExtendedLink
                    extendedLink={element.link_extended}
                    btnType={buttonType}
                  />
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ImageGridCard;
